import { Divider, DropdownButton, DropdownButtonItem, DropdownButtonPopover, DropdownButtonTrigger } from '@d-edge/overnight-hotelier-react'
import { useEffect, useState } from 'react'
import { NavLink, Outlet } from 'react-router'
import { useConfig, useSettingsTables } from '@/api/queries'
import { signOut, useSession } from '@/auth/session'
import { Badge } from '@/components/ui/Badge'
import { BrandLogo } from '@/components/ui/BrandLogo'
import { Button, IconButton } from '@/components/ui/Button'
import { Icons } from '@/components/ui/Icons'
import { AttentionButton } from '@/features/attention/AttentionButton'
import { DelayBanner } from '@/features/ratescreener/DelayBanner'
import { DisableHotelDialog } from '@/features/hotels/DisableHotelDialog'
import { PosScriptsDialog } from '@/features/posScripts/PosScriptsDialog'
import { IpUsageDialog } from '@/features/proxies/IpUsageDialog'
import { ScheduledJobsDialog } from '@/features/scheduler/ScheduledJobsDialog'
import { SettingsTableDialog } from '@/features/settings/SettingsTableDialog'
import { QueueMessageDialog } from '@/features/tools/QueueMessageDialog'
import styles from './AppLayout.module.css'
import { useRefresh } from './RefreshContext'
import { useTheme } from './theme'

type Tool = { kind: 'disable-hotel' } | { kind: 'ip-usage' } | { kind: 'jobs' } | { kind: 'pos-scripts' } | { kind: 'queue-message' } | { kind: 'settings'; tableKey: string }

export function AppLayout() {
  const { data: config } = useConfig()
  const [tool, setTool] = useState<Tool | null>(null)

  useEffect(() => {
    if (config?.isProduction) document.title = '🚨 DI Cockpit · production'
  }, [config])

  return (
    <div className={styles.shell}>
      <div className={styles.stickyTop}>
        <header className={`${styles.topbar} ${config?.isProduction ? styles.production : ''}`}>
          <NavLink to="/" className={styles.brand}>
            <BrandLogo className={styles.logo} />
            <span className={styles.navLabel}>Cockpit</span>
          </NavLink>
          {config && (
            <Badge tone={config.isProduction ? 'danger' : config.usesFakeData ? 'warn' : 'info'} title={config.usesFakeData ? 'The API serves sample data' : undefined}>
              {config.environment}
              {config.usesFakeData ? ' · fake data' : ''}
            </Badge>
          )}
          <nav className={styles.nav}>
            <NavLink to="/" end className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navActive : ''}`}>
              Dashboard
            </NavLink>
            <NavLink to="/monitoring" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navActive : ''}`}>
              Monitoring
            </NavLink>
            <NavLink to="/resources" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navActive : ''}`}>
              Resources
            </NavLink>
          </nav>
          <div className={styles.spacer} />
          <RefreshControls />
          <AttentionButton className={styles.attention} />
          <ToolsMenu onOpen={setTool} />
          <ThemeToggle />
          <SignOutButton />
        </header>
        <DelayBanner />
      </div>

      <main className={styles.main}>
        <Outlet />
      </main>

      {tool?.kind === 'disable-hotel' && <DisableHotelDialog onClose={() => setTool(null)} />}
      {tool?.kind === 'ip-usage' && <IpUsageDialog onClose={() => setTool(null)} />}
      {tool?.kind === 'jobs' && <ScheduledJobsDialog onClose={() => setTool(null)} />}
      {tool?.kind === 'pos-scripts' && <PosScriptsDialog onClose={() => setTool(null)} />}
      {tool?.kind === 'queue-message' && <QueueMessageDialog onClose={() => setTool(null)} />}
      {tool?.kind === 'settings' && <SettingsTableDialog tableKey={tool.tableKey} onClose={() => setTool(null)} />}
    </div>
  )
}

function RefreshControls() {
  const { enabled, countdown, paused, refreshNow, toggleEnabled } = useRefresh()
  return (
    <div className={styles.refresh}>
      <span className={styles.countdown}>{!enabled ? 'Auto refresh off' : paused ? 'Refresh paused' : `Refresh in ${countdown}s`}</span>
      <Button size="sm" onClick={toggleEnabled} title={enabled ? 'Pause the automatic refresh' : 'Resume the automatic refresh'}>
        {enabled ? 'Pause' : 'Resume'}
      </Button>
      <IconButton label="Refresh now" onClick={refreshNow}>
        <Icons.Refresh size={16} />
      </IconButton>
    </div>
  )
}

function ToolsMenu({ onOpen }: { onOpen: (tool: Tool) => void }) {
  const { data: tables } = useSettingsTables()
  return (
    <DropdownButton position="bottom-end" className={styles.tools}>
      <DropdownButtonTrigger>Tools</DropdownButtonTrigger>
      <DropdownButtonPopover className={styles.toolsPopover}>
        <DropdownButtonItem iconName="clock" onClick={() => onOpen({ kind: 'jobs' })}>
          Scheduled jobs
        </DropdownButtonItem>
        <DropdownButtonItem iconName="file-contract" onClick={() => onOpen({ kind: 'pos-scripts' })}>
          PoS scripts
        </DropdownButtonItem>
        <DropdownButtonItem iconName="ban" onClick={() => onOpen({ kind: 'disable-hotel' })}>
          Disable a hotel
        </DropdownButtonItem>
        <DropdownButtonItem iconName="chart-line" onClick={() => onOpen({ kind: 'ip-usage' })}>
          IP usage
        </DropdownButtonItem>
        <DropdownButtonItem iconName="envelope" onClick={() => onOpen({ kind: 'queue-message' })}>
          Compress / decompress queue message
        </DropdownButtonItem>
        {tables && tables.length > 0 && <Divider />}
        {tables?.map((table) => (
          <DropdownButtonItem key={table.key} iconName="table" onClick={() => onOpen({ kind: 'settings', tableKey: table.key })}>
            {table.name}
          </DropdownButtonItem>
        ))}
      </DropdownButtonPopover>
    </DropdownButton>
  )
}

/** Only rendered with Google sign-in; the label names the account. */
function SignOutButton() {
  const session = useSession()
  if (!session) return null
  return (
    <IconButton label={`Sign out ${session.email}`} onClick={signOut}>
      <Icons.SignOut size={16} />
    </IconButton>
  )
}

function ThemeToggle() {
  const [theme, setTheme] = useTheme()
  const next = theme === 'dark' ? 'light' : 'dark'
  return (
    <IconButton label={`Switch to ${next} theme`} onClick={() => setTheme(next)}>
      {theme === 'dark' ? <Icons.Sun size={16} /> : <Icons.Moon size={16} />}
    </IconButton>
  )
}
